import { useCallback, useMemo } from 'react';
import { useAuth } from 'src/auth';
import { getValidatedFromLocalStorage, setJSONInLocalStorage } from 'src/utils/safeStorage';
import { z } from 'zod';

const lastTagLayoutSchema = z.object({ id: z.number().int().positive() });

/**
 * The tag layout this user last created pallet tags with, per organization on this browser.
 * Without it the drawer opens on the first layout of the list (oldest first), which is rarely
 * the one a packer prints with (TAS-3660: "it always reverts back to PTI").
 */
export function useLastTagLayout(): { lastTagLayoutId: number | null; rememberTagLayout: (id: number) => void } {
  const { currentUser } = useAuth();
  const storageKey = useMemo(
    () => (currentUser ? `palletTags.lastTagLayout:${currentUser.id}:${currentUser.organizationId ?? ''}` : null),
    [currentUser]
  );
  const lastTagLayoutId = useMemo(
    () => (storageKey ? (getValidatedFromLocalStorage(storageKey, lastTagLayoutSchema)?.id ?? null) : null),
    [storageKey]
  );
  const rememberTagLayout = useCallback(
    (id: number) => {
      if (storageKey) setJSONInLocalStorage(storageKey, { id });
    },
    [storageKey]
  );

  return { lastTagLayoutId, rememberTagLayout };
}

/**
 * This browser's remembered layout when the list has it, else the one the organization last printed
 * with (`isLastUsed`, so a new device or browser opens on it too), else the list's first, else the
 * built-in default.
 */
export function initialTagLayout<T extends { id?: number | null; isLastUsed?: boolean | null }>(
  tagLayouts: readonly T[] | undefined,
  lastTagLayoutId: number | null,
  fallback: T
): T {
  return (
    tagLayouts?.find((layout) => layout.id === lastTagLayoutId) ??
    tagLayouts?.find((layout) => layout.isLastUsed) ??
    tagLayouts?.[0] ??
    fallback
  );
}
